/**
 * Hlavní entry-point pro chart data. Po fixu (viz docs/plans/CHART-DATA-REFACTOR-FIX.md):
 *   1. BATCHED fetch — jeden HTTP request per wave pokrývající VŠECHNY chybějící
 *      (topicId, tile) páry (místo N×M fan-out v useEffect smyčce).
 *   2. 500ms debounce na bounds/resolution změny (mount = 0ms, subsequent = 500ms).
 *      Pan/zoom updatuje bounds OKAMŽITĚ (60fps smooth visual transform), ale fetch
 *      počká dokud uživatel nedopanuje.
 *   3. Boolean topicy VŽDY raw resolution (klient i server) — uživatel chce všechny
 *      transitions. Tile keys pak používají "raw" bez ohledu na pixelWidth.
 *   4. Retry s exp backoff (1s/2s/4s) pro síťové chyby — žádný permanent gap.
 *   5. JEDEN AbortController pro celý batch — pan tick aboruje předchozí (jediný)
 *      request, ne N×M paralelních.
 *
 *  Live WS tail (useValuesStore.byTopic) se append-uje k raw series stejně jako dřív.
 */

import { useEffect, useMemo, useRef } from "react";
import axios from "axios";
import { postChartDataBatched } from "@client/api/endpoints";
import {
  isTileFresh,
  makeTileKey,
  resolveResolution,
  tileRangesForRange,
  type ChartResolutionKey,
} from "@client/lib/chartBucketing";
import { useChartStore, type ChartPoint } from "@client/store/chart";
import { useMetricMetaStore } from "@client/store/metricMeta";
import { useValuesStore } from "@client/store/values";
import { useUserStore } from "@client/store/user";

export interface UseChartDataOptions {
  topicIds: string[];
  start: number;
  end: number;
  /** Šířka chartu v px — určuje server-side downsample density. */
  pixelWidth: number;
  /** False → hook pause (collapsed wrapper, off-screen). Default true. */
  enabled?: boolean;
}

export interface UseChartDataResult {
  /** Per-topic seřazené body (sloučené tiles + tail WS). Klíč = topicId. */
  series: Map<string, ChartPoint[]>;
  /** Per-topic resolution (klient ví, jestli má vykreslit Area band navíc k Line). */
  resolution: Map<string, ChartResolutionKey>;
  /** True dokud běží fetch pro některý ze subscribed tiles. */
  loading: boolean;
}

const FETCH_DEBOUNCE_MS = 500;
const RETRY_DELAYS_MS = [1000, 2000, 4000];

type TopicMetaLite = { topic: string; valueType?: string };

/** Vrátí effective resolution pro daný topic. Boolean topicy vždy "raw". */
const effectiveResolutionForTopic = (
  topicId: string,
  baseRes: ChartResolutionKey,
  topicsMeta: Map<string, TopicMetaLite>
): ChartResolutionKey => {
  const meta = topicsMeta.get(topicId);
  if (meta?.valueType === "BOOLEAN") return "raw";
  return baseRes;
};

/** Dedup tile ranges (stejný start+end+resolution = duplicate). */
const dedupTiles = (
  tiles: { start: number; end: number; resolution: ChartResolutionKey }[]
): { start: number; end: number; resolution: ChartResolutionKey }[] => {
  const seen = new Set<string>();
  const out: { start: number; end: number; resolution: ChartResolutionKey }[] = [];
  for (const t of tiles) {
    const k = `${t.start}|${t.end}|${t.resolution}`;
    if (seen.has(k)) continue;
    seen.add(k);
    out.push(t);
  }
  return out;
};

/**
 * Spustí batched fetch s retry. Vrací promise která rezolvne až po success
 * NEBO po vyčerpání retries (nikdy nethrowuje pokud signal není aborted).
 */
const fetchBatchedWithRetry = async (
  topicIds: string[],
  tiles: { start: number; end: number; resolution: ChartResolutionKey }[],
  signal: AbortSignal,
  attempt = 0
): Promise<ChartBatchedResponse | null> => {
  try {
    const res = await postChartDataBatched({ topicIds, tiles }, signal);
    if (!res.success) return null;
    return res.responseObject;
  } catch (err) {
    if (axios.isCancel(err)) return null;
    if (attempt >= RETRY_DELAYS_MS.length) {
      console.error("Batched chart fetch failed (retries exhausted)", err);
      return null;
    }
    const delay = RETRY_DELAYS_MS[attempt];
    await new Promise((r) => setTimeout(r, delay));
    if (signal.aborted) return null;
    return fetchBatchedWithRetry(topicIds, tiles, signal, attempt + 1);
  }
};

export const useChartData = (opts: UseChartDataOptions): UseChartDataResult => {
  const { topicIds, start, end, pixelWidth, enabled = true } = opts;
  const tiles = useChartStore((s) => s.tiles);
  const inFlight = useChartStore((s) => s.inFlight);
  const byTopic = useValuesStore((s) => s.byTopic);
  const metaMap = useMetricMetaStore((s) => s.meta);
  const user = useUserStore((s) => s.user);

  const lastFetchKeysRef = useRef<Set<string>>(new Set());
  const isFirstFetchRef = useRef(true);
  const pendingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingControllerRef = useRef<AbortController | null>(null);

  const topicsKey = topicIds.join("|");

  // Topic metadata Map — pro O(1) lookup boolean check.
  // Memoizace přes velikost+hash topic ID prvků — `metaMap` reference se mění
  // s každým upsertem, ale boolean rozdělení záleží jen na valueType subsetu
  // pro topicy ve scope.
  const topicsMetaMap = useMemo(() => {
    const m = new Map<string, TopicMetaLite>();
    for (const id of topicIds) {
      const t = metaMap.get(id);
      if (t) m.set(id, { topic: t.topic, valueType: t.valueType });
    }
    return m;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [metaMap, topicsKey]);

  // Base resolution (pro FLOAT topicy). Boolean topicy se override-nou na "raw".
  const baseResolution = useMemo(
    () => resolveResolution(end - start, pixelWidth),
    [end, start, pixelWidth]
  );

  // Trigger fetch — debounced. Pan/zoom updatuje bounds OKAMŽITĚ (smooth visual),
  // ale fetch počká FETCH_DEBOUNCE_MS po posledním ticku. Mount = 0ms (init load).
  useEffect(() => {
    if (!enabled || !user) {
      if (pendingTimeoutRef.current) {
        clearTimeout(pendingTimeoutRef.current);
        pendingTimeoutRef.current = null;
      }
      if (pendingControllerRef.current) {
        pendingControllerRef.current.abort();
        pendingControllerRef.current = null;
      }
      for (const key of lastFetchKeysRef.current) useChartStore.getState().abortInFlight(key);
      lastFetchKeysRef.current = new Set();
      return;
    }

    const delay = isFirstFetchRef.current ? 0 : FETCH_DEBOUNCE_MS;
    if (pendingTimeoutRef.current) clearTimeout(pendingTimeoutRef.current);

    pendingTimeoutRef.current = setTimeout(() => {
      pendingTimeoutRef.current = null;
      isFirstFetchRef.current = false;

      const store = useChartStore.getState();
      const now = Date.now();

      // Per topic spočítej tiles (s respektem k effective resolution). Posbírej
      // chybějící / stale tiles do flat seznamu.
      type Missing = { topicId: string; key: string; tile: { start: number; end: number }; res: ChartResolutionKey };
      const missing: Missing[] = [];
      const allCurrentKeys = new Set<string>();

      for (const topicId of topicIds) {
        const res = effectiveResolutionForTopic(topicId, baseResolution, topicsMetaMap);
        const required = tileRangesForRange(res, start, end);
        for (const tile of required) {
          const key = makeTileKey(topicId, res, tile.start);
          allCurrentKeys.add(key);
          const cached = store.peekTile(key);
          const fresh = cached && isTileFresh(cached.tileEnd, cached.fetchedAt, now);
          if (fresh) continue;
          if (store.isInFlight(key)) continue;
          missing.push({ topicId, key, tile, res });
        }
      }

      // Abort tiles, které už nepotřebujeme (user změnil bounds)
      for (const oldKey of lastFetchKeysRef.current) {
        if (!allCurrentKeys.has(oldKey)) useChartStore.getState().abortInFlight(oldKey);
      }
      lastFetchKeysRef.current = allCurrentKeys;

      if (missing.length === 0) return;

      // JEDEN AbortController pro celý batch — pan tick aboruje předchozí batched
      // fetch, ne N×M paralelních.
      const controller = new AbortController();
      pendingControllerRef.current = controller;
      for (const m of missing) store.setInFlight(m.key, controller);

      // Group tiles by resolution. Server přijme JEDEN seznam tiles[] kde každý
      // má vlastní resolution; topicIds jsou unique union (server per-topic vybere
      // effective resolution sám — boolean topicy override-ne na raw, ale stále
      // potřebuje vědět které topicy jsou v scope).
      const uniqueTopicIds = [...new Set(missing.map((m) => m.topicId))];
      const uniqueTiles = dedupTiles(missing.map((m) => ({ start: m.tile.start, end: m.tile.end, resolution: m.res })));

      void fetchBatchedWithRetry(uniqueTopicIds, uniqueTiles, controller.signal).then((response) => {
        if (!response) return;
        const fetchedAt = Date.now();
        const writeStore = useChartStore.getState();
        // Zapiš každý tile do cache. Server může vrátit i tiles pro topicy které
        // jsme nepožadovali (např. pokud klient vyžádal multi-topic batch, ale
        // tile měl prázdné data — vrátíme prázdný array, klient ho cachuje).
        for (const tileWire of response.tiles) {
          const key = makeTileKey(tileWire.topicId, tileWire.resolution, tileWire.tileStart);
          writeStore.putTile(key, {
            topicId: tileWire.topicId,
            resolution: tileWire.resolution,
            tileStart: tileWire.tileStart,
            tileEnd: tileWire.tileEnd,
            points: tileWire.points.map((p) => ({
              ts: p.ts,
              v: p.v,
              min: p.min,
              max: p.max,
              count: p.count,
            })),
            fetchedAt,
            lastAccessedAt: fetchedAt,
            boundaryBefore: tileWire.boundaryBefore,
            boundaryAfter: tileWire.boundaryAfter,
          });
        }
      }).finally(() => {
        for (const m of missing) useChartStore.getState().clearInFlight(m.key);
        if (pendingControllerRef.current === controller) {
          pendingControllerRef.current = null;
        }
      });
    }, delay);

    return () => {
      if (pendingTimeoutRef.current) {
        clearTimeout(pendingTimeoutRef.current);
        pendingTimeoutRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [topicsKey, start, end, baseResolution, enabled, user, topicsMetaMap]);

  // Cleanup na unmount
  useEffect(() => {
    return () => {
      if (pendingTimeoutRef.current) clearTimeout(pendingTimeoutRef.current);
      if (pendingControllerRef.current) pendingControllerRef.current.abort();
      for (const key of lastFetchKeysRef.current) useChartStore.getState().abortInFlight(key);
    };
  }, []);

  // Slož series — per topic projdi všechny tiles (s effective resolution) překrývající
  // [start, end] + tail z byTopic. Boundary values přidat jako extra body.
  return useMemo(() => {
    const series = new Map<string, ChartPoint[]>();
    const resolutionMap = new Map<string, ChartResolutionKey>();

    if (!enabled) {
      for (const topicId of topicIds) {
        series.set(topicId, []);
        resolutionMap.set(topicId, baseResolution);
      }
      return { series, resolution: resolutionMap, loading: false };
    }

    for (const topicId of topicIds) {
      const res = effectiveResolutionForTopic(topicId, baseResolution, topicsMetaMap);
      const merged: ChartPoint[] = [];
      const required = tileRangesForRange(res, start, end);

      // Server posílá per-tile boundaries (hodnota < tile.start a > tile.end).
      // Pushneme všechny do merged + dedupe by ts na konci. U sousedních tiles
      // boundary tile B = poslední bod tile A → dedup je deduplikuje. Pro KRAJNÍ
      // tile (první/poslední v required) boundary obsahuje hodnotu MIMO všechny
      // required tiles → klíčové pro chart edge interpolaci (jinak by chart
      // line končila tam kde poslední tile point, ne na okraji visible window).
      for (const tileRange of required) {
        const key = makeTileKey(topicId, res, tileRange.start);
        const tile = tiles.get(key);
        if (!tile) continue;
        for (const p of tile.points) merged.push(p);
        if (tile.boundaryBefore) merged.push({ ts: tile.boundaryBefore.ts, v: tile.boundaryBefore.v });
        if (tile.boundaryAfter) merged.push({ ts: tile.boundaryAfter.ts, v: tile.boundaryAfter.v });
      }

      // Fallback proti "zmizení" grafu při zoomu: když cílové rozlišení ještě nemá žádné tiles
      // (typicky hned po překročení prahu rozlišení, než doběhne 500ms-debounced fetch), použij
      // JAKÉKOLI cached tiles tohoto topicu překrývající [start, end] (i jiného rozlišení). Stará
      // čára se ořízne na bounds (allowDataOverflow) → graf nezmizí, jen ukáže náhled, dokud
      // nedorazí cílové rozlišení. Scan běží jen při prázdném merged (= krátký přechod).
      if (merged.length === 0) {
        for (const tile of tiles.values()) {
          if (tile.topicId !== topicId) continue;
          if (tile.tileEnd < start || tile.tileStart > end) continue;
          for (const p of tile.points) merged.push(p);
        }
      }

      // Live tail extension — prodlouží chart line k pravému okraji pro VŠECHNY resolutions.
      // Bez tohohle bloku bucketed series končí na posledním server-vypočítaném bucket pointu,
      // což po WS-TRAFFIC-REDUCTION (heartbeats místo every-value WS) ponechá ~30 min gap na
      // pravém okraji (viz CHART-TAIL-EXTENSION plán).
      //   Raw: použij live.value (latest instantaneous z WS).
      //   Bucketed/stat: extrapoluj horizontálně z posledního pointu (lastPoint.v) — heartbeat
      //   jen potvrzuje že topic žije, neposílá nový sample. Bucket-avg ≠ instant value by
      //   dělalo vizuální skok.
      // Tail ts = min(live.timestamp, end) → nikdy neextrapolujeme za visible window
      // (user může panovat do historie) ani za confirmed-alive timestamp.
      const live = byTopic.get(topicId);
      if (live && merged.length > 0) {
        const lastPoint = merged.reduce((acc, p) => (p.ts > acc.ts ? p : acc), merged[0]);
        const liveTs = live.timestamp.getTime();
        const tailTs = Math.min(liveTs, end);
        if (tailTs > lastPoint.ts && tailTs >= start) {
          const tailV = res === "raw" ? (live.value as number | boolean) : lastPoint.v;
          // Pro bucketed s nullovým posledním bodem (= žádná data v bucketu) nemá smysl
          // extrapolovat null — chart by jen extended gap. Skip.
          if (tailV !== null) merged.push({ ts: tailTs, v: tailV });
        }
      }

      // Dedupe + sort
      const dedup = new Map<number, ChartPoint>();
      for (const p of merged) dedup.set(p.ts, p);
      const sorted = [...dedup.values()].sort((a, b) => a.ts - b.ts);

      series.set(topicId, sorted);
      resolutionMap.set(topicId, res);
    }

    const loading = [...inFlight.keys()].some((k) => {
      const [t] = k.split("|");
      return topicIds.includes(t);
    });

    return { series, resolution: resolutionMap, loading };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tiles, inFlight, byTopic, topicsKey, start, end, baseResolution, enabled, topicsMetaMap]);
};
